import { useTranslation } from '@shared/core';
import { useCallback, useMemo } from 'react';

// @todo [TRAV-2250] Update with actual tags from backend once backend support is available
const VALID_BEST_FOR_TAGS = [
  // New tags in SCREAMING_SNAKE_CASE format. Forward-compatible with future backend additions.
  'BUSINESS_TRAVELERS',
  'COUPLES',
  'DIGITAL_NOMADS',
  'FAMILIES',
  'FIRST_TIME_VISITORS',
  'LONG_STAYS',
  'REMOTE_WORK',
  'SHORT_STAYS',
  'SOLO_TRAVELERS',
  'WEEKEND_GETAWAYS',

  // Legacy tags in snake_case/lowercase format. Maintained for backward compatibility.
  'families',
  'couples',
  'art_lovers',
  'short_commutes',
  'walkable to venue',
] as const;

type BestForTag = (typeof VALID_BEST_FOR_TAGS)[number];

// Static Set for O(1) lookup
const VALID_BEST_FOR_TAGS_SET: ReadonlySet<string> = new Set(VALID_BEST_FOR_TAGS);

export const useStayZoneBestForTags = () => {
  const { tq } = useTranslation('stayZonesGuest');

  const bestForTagMap: Readonly<Record<BestForTag, { emoji: string; label: string }>> = useMemo(
    () => ({
      // New tags in SCREAMING_SNAKE_CASE format. Forward-compatible with future backend additions.
      BUSINESS_TRAVELERS: {
        emoji: '💼',
        label: tq`Best for business travelers`,
      },
      COUPLES: {
        emoji: '💑',
        label: tq`Best for couples`,
      },
      DIGITAL_NOMADS: {
        emoji: '✈️',
        label: tq`Best for digital nomads`,
      },
      FAMILIES: {
        emoji: '🏡',
        label: tq`Best for families`,
      },
      FIRST_TIME_VISITORS: {
        emoji: '👋',
        label: tq`Best for first time visitors`,
      },
      LONG_STAYS: {
        emoji: '📅',
        label: tq`Best for long stays`,
      },
      REMOTE_WORK: {
        emoji: '🖥️',
        label: tq`Best for remote work`,
      },
      SHORT_STAYS: {
        emoji: '⏱️',
        label: tq`Best for short stays`,
      },
      SOLO_TRAVELERS: {
        emoji: '👤',
        label: tq`Best for solo travelers`,
      },
      WEEKEND_GETAWAYS: {
        emoji: '🎒',
        label: tq`Best for weekend getaways`,
      },

      // Legacy tags in snake_case/lowercase format. Maintained for backward compatibility.
      families: {
        emoji: '🏡',
        label: tq`Best for families`,
      },
      couples: {
        emoji: '💑',
        label: tq`Best for couples`,
      },
      art_lovers: {
        emoji: '🎨',
        label: tq`Best for art lovers`,
      },
      short_commutes: {
        emoji: '🚶',
        label: tq`Best for walkable areas`,
      },
      'walkable to venue': {
        emoji: '🚶',
        label: tq`Best for walkable areas`,
      },
    }),
    [tq],
  );

  const parseBestForTag = useCallback(
    (bestForTags: ReadonlyArray<string>) => {
      for (const tag of bestForTags) {
        if (VALID_BEST_FOR_TAGS_SET.has(tag)) {
          return bestForTagMap[tag as BestForTag];
        }
      }
      return undefined;
    },
    [bestForTagMap],
  );

  return useMemo(
    () => ({
      bestForTagMap,
      parseBestForTag,
    }),
    [bestForTagMap, parseBestForTag],
  );
};
